/**
 * Margin chart: shared between the Group, All Entities, single-entity, All
 * Personal and personal-entity dashboards. Same pattern as the Property
 * Portfolio dashboard's own "Margin (GBP)" chart (the reference this was
 * built from): Net Margin as bars stacked by segment, Gross Margin as a
 * single combined line overlay. Always rendered directly below that
 * dashboard's Gross Revenue chart, which now carries revenue bars only --
 * the margin lines that used to live on that chart live here instead.
 *
 * `segments` must be the exact same list (key/label/color, in stacking
 * order) the paired Gross Revenue chart above it uses, so the two charts
 * read as one system and degrade the same way (e.g. a Personal dashboard's
 * single populated segment).
 */
import React, { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, LabelList,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCompact, formatCurrencyFull, shouldSkipDataLabels } from '../../lib/chartFormatters';
import { ForecastCaveat, ForecastBarShape, forecastTotalLabelContent } from '../../lib/forecastChartUtils';
import { ForecastTotalsFooter } from './ForecastTotalsFooter';

const GROSS_MARGIN_COLOR = '#F59E0B';

function MarginChartTooltip({ active, payload, label, segments, displayCurrency }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm min-w-[220px]">
      <p className="font-semibold mb-2">{label}</p>
      {segments.map((seg) => {
        // Actual and forecast are separate lines: a month can carry both
        // (current month = actual to date + forecast remainder). Showing only
        // the forecast hid the actual, so the lines never added up to the
        // net total (Fan, 2026-10-27).
        const forecastVal = point[`${seg.label}_forecast`];
        const actualVal = point[seg.label] || 0;
        const hasForecast = forecastVal != null;
        return (
          <React.Fragment key={seg.key}>
            {(!hasForecast || actualVal !== 0) && (
              <div className="flex justify-between gap-6">
                <span style={{ color: seg.color }}>{seg.label}</span>
                <span className="font-mono">{formatCurrencyFull(actualVal, displayCurrency)}</span>
              </div>
            )}
            {hasForecast && (
              <div className="flex justify-between gap-6">
                <span style={{ color: seg.color }}>{seg.label} (forecast)</span>
                <span className="font-mono">{formatCurrencyFull(forecastVal, displayCurrency)}</span>
              </div>
            )}
          </React.Fragment>
        );
      })}
      {point._ppCostEstimate != null && (
        <div className="text-xs text-muted-foreground mt-1 max-w-[260px]">
          Portfolio includes {formatCurrencyFull(point._ppCostEstimate, displayCurrency)} estimated
          UK statement costs (CWC statement not in yet)
        </div>
      )}
      <div className="flex justify-between gap-6 border-t pt-1 mt-2 font-medium">
        <span>Net (combined)</span>
        <span className="font-mono">{formatCurrencyFull(point._total || 0, displayCurrency)}</span>
      </div>
      {segments.some((seg) => point[`${seg.label}_forecast`] != null) && (
        <div className="flex justify-between gap-6 mt-1 font-medium">
          <span>Net incl. forecast</span>
          <span className="font-mono">{formatCurrencyFull(
            (point._total || 0) + segments.reduce((s, seg) => s + (point[`${seg.label}_forecast`] || 0), 0),
            displayCurrency)}</span>
        </div>
      )}
      {point._grossMargin != null && (
        <div className="flex justify-between gap-6 mt-1 font-medium">
          <span style={{ color: GROSS_MARGIN_COLOR }}>Gross (combined)</span>
          <span className="font-mono">{formatCurrencyFull(point._grossMargin, displayCurrency)}</span>
        </div>
      )}
    </div>
  );
}

export function MarginChart({ chartData, segments, displayCurrency, year }) {
  const ytd = useMemo(() => {
    const out = { _grossCombined: 0, _netCombined: 0 };
    (segments || []).forEach((seg) => { out[seg.label] = 0; });
    (chartData || []).forEach((row) => {
      (segments || []).forEach((seg) => { out[seg.label] += row[seg.label] || 0; });
      out._netCombined += row._total || 0;
      out._grossCombined += row._grossMargin || 0;
    });
    return out;
  }, [chartData, segments]);

  if (!chartData || chartData.length === 0 || !segments || segments.length === 0) return null;

  const hasForecast = chartData.some((d) => d._isForecast);
  const skipLabels = shouldSkipDataLabels(chartData);
  const lastSegment = segments[segments.length - 1];
  const segmentLabelFormatter = (v) => (!skipLabels && v ? formatCompact(v, displayCurrency) : '');

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Margin{year ? ` (monthly) ${year}` : ''}</CardTitle>
        <ForecastCaveat show={hasForecast} />
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={340}>
          <ComposedChart data={chartData} margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis
              tickFormatter={(v) => formatCompact(v, displayCurrency)}
              tick={{ fontSize: 11 }}
              width={60}
            />
            <Tooltip content={<MarginChartTooltip segments={segments} displayCurrency={displayCurrency} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {segments.map((seg) => (
              <Bar
                key={seg.key}
                dataKey={seg.label}
                stackId="margin"
                fill={seg.color}
                radius={seg.key === lastSegment.key ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                shape={<ForecastBarShape data={chartData} />}
              >
                <LabelList dataKey={seg.label} position="inside" fontSize={10}
                  formatter={segmentLabelFormatter} fill="#fff" />
                {seg.key === lastSegment.key && (
                  <LabelList
                    position="top"
                    content={(lp) => {
                      if (skipLabels) return null;
                      const { x, y, width, index } = lp;
                      const row = chartData[index];
                      if (!row || !row._total) return null;
                      return (
                        <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={700}
                          fill="hsl(var(--foreground))">
                          {formatCompact(row._total, displayCurrency)}
                        </text>
                      );
                    }}
                  />
                )}
              </Bar>
            ))}
            {segments.map((seg) => (
              chartData.some((d) => d[`${seg.label}_forecast`] != null) ? (
                <Bar
                  key={`${seg.key}_forecast`}
                  dataKey={`${seg.label}_forecast`}
                  name={`${seg.label} (forecast)`}
                  stackId="margin"
                  fill={seg.color}
                  fillOpacity={0.35}
                  stroke={seg.color}
                  strokeDasharray="4 2"
                  strokeWidth={1}
                  radius={[2, 2, 0, 0]}
                >
                  <LabelList dataKey={`${seg.label}_forecast`} position="inside" fontSize={10}
                    formatter={segmentLabelFormatter} fill={seg.color} />
                  {/* Stacked forecast TOTAL on the topmost forecast segment
                      of each month (lib/groupGrossRevenueChart.js
                      annotateForecastTotals). */}
                  <LabelList position="top"
                    content={forecastTotalLabelContent(chartData, `${seg.label}_forecast`,
                      { formatter: (v) => formatCompact(v, displayCurrency), fill: seg.color })} />
                </Bar>
              ) : null
            ))}
            <Line
              type="monotone"
              dataKey="_grossMargin"
              name="Gross Margin"
              stroke={GROSS_MARGIN_COLOR}
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls={false}
            >
              <LabelList position="top" fontSize={9} fill={GROSS_MARGIN_COLOR}
                formatter={segmentLabelFormatter} />
            </Line>
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-2 text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
          <span>YTD Gross Margin:</span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: GROSS_MARGIN_COLOR }} />
            <span>Combined</span>
            <span className="font-mono font-semibold text-foreground">
              {formatCompact(ytd._grossCombined, displayCurrency)}
            </span>
          </span>
        </div>
        <div className="mt-1 text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
          <span>YTD Net Margin:</span>
          {segments.map((seg) => (
            <span key={seg.key} className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: seg.color }} />
              <span>{seg.label}</span>
              <span className="font-mono font-semibold text-foreground">
                {formatCompact(ytd[seg.label], displayCurrency)}
              </span>
            </span>
          ))}
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-foreground" />
            <span>Combined</span>
            <span className="font-mono font-semibold text-foreground">
              {formatCompact(ytd._netCombined, displayCurrency)}
            </span>
          </span>
        </div>
        <ForecastTotalsFooter
          data={chartData}
          series={(segments || []).map((seg) => ({ key: seg.label, label: seg.label, color: seg.color }))}
          currency={displayCurrency}
          label="Net Margin"
        />
      </CardContent>
    </Card>
  );
}
